/**
 * Реестр юридических документов igra.now.
 *
 * Тексты НЕ живут в локалях и не размазаны по ключам: юрист правит один файл
 * целиком, а не тридцать строк перевода. Каждый документ — обычный Markdown в
 * `src/sites/IgraNow/docs/legal/`, тот самый файл, который читает и правит
 * юрист; сюда он попадает `?raw`-импортом и инлайнится в бандл при сборке.
 *
 * Почему именно так, а не через локали или бэкенд-страницы:
 *  - локали дробят документ на ключи, и правка одной фразы превращается в
 *    поиск по дереву переводов; после инцидентов с пропавшими ключами
 *    (HANDOFF §9) держать в них юридический текст просто опасно;
 *  - бэкендовые `/app/law/*` требуют наполнения контентной подсистемы сайта,
 *    которого для igra.now нет, — именно поэтому они и отдают 404;
 *  - `?raw` не заводит второй копии текста: источник правды один, и он же
 *    лежит в репозитории рядом с остальными документами спринта.
 *
 * Служебная врезка front-matter (статус черновика, кому и что проверять)
 * снимается здесь и на страницу не попадает — она адресована юристу, а не
 * читателю сайта.
 */
import offerRaw from '../../../../../../sites/IgraNow/docs/legal/oferta.md?raw'
import tariffsRaw from '../../../../../../sites/IgraNow/docs/legal/tariffs.md?raw'
import recurrentRaw from '../../../../../../sites/IgraNow/docs/legal/recurrent.md?raw'
import cancelRaw from '../../../../../../sites/IgraNow/docs/legal/subscription-cancel.md?raw'
import refundRaw from '../../../../../../sites/IgraNow/docs/legal/refund.md?raw'
import privacyRaw from '../../../../../../sites/IgraNow/docs/legal/privacy.md?raw'
import servicesRaw from '../../../../../../sites/IgraNow/docs/legal/services.md?raw'

export interface LegalDoc {
  slug: string
  title: string
  updated: string
  status: string
  markdown: string
}

/** Порядок осмысленный: сначала договор, потом деньги, потом отмена и данные. */
export const LEGAL_SLUGS = [
  'offer',
  'tariffs',
  'recurrent',
  'cancel',
  'refund',
  'privacy',
  'services',
] as const

export type LegalSlug = (typeof LEGAL_SLUGS)[number]

const RAW_BY_SLUG: Record<LegalSlug, string> = {
  offer: offerRaw,
  tariffs: tariffsRaw,
  recurrent: recurrentRaw,
  cancel: cancelRaw,
  refund: refundRaw,
  privacy: privacyRaw,
  services: servicesRaw,
}

const FRONT_MATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n/

function frontMatterField(block: string, field: string): string {
  const match = block.match(new RegExp(`^${field}:\\s*(.+)$`, 'm'))
  return match === null ? '' : match[1].trim()
}

export function parseDoc(slug: string, raw: string): LegalDoc {
  const header = raw.match(FRONT_MATTER)
  const block = header === null ? '' : header[1]

  return {
    slug,
    title: frontMatterField(block, 'title'),
    updated: frontMatterField(block, 'updated'),
    status: frontMatterField(block, 'status'),
    markdown: header === null ? raw : raw.slice(header[0].length),
  }
}

export const LEGAL_DOCS: Record<LegalSlug, LegalDoc> = LEGAL_SLUGS.reduce(
  (acc, slug) => {
    acc[slug] = parseDoc(slug, RAW_BY_SLUG[slug])
    return acc
  },
  {} as Record<LegalSlug, LegalDoc>,
)

export const LEGAL_INDEX_PATH = '/law'

export function legalPath(slug: string): string {
  return `${LEGAL_INDEX_PATH}/${slug}`
}

/**
 * Публичный сайт ключей (`key.igra.click`) собирается отдельной точкой входа
 * без роутера, поэтому ссылки оттуда обязаны быть абсолютными — относительный
 * `/law/offer` увёл бы человека на несуществующую страницу чужого домена.
 */
export const LEGAL_ORIGIN = 'https://igra.now'

export function legalUrl(slug: string): string {
  return `${LEGAL_ORIGIN}${legalPath(slug)}`
}

export function findLegalDoc(slug: string): LegalDoc | null {
  return (LEGAL_SLUGS as readonly string[]).includes(slug) ? LEGAL_DOCS[slug as LegalSlug] : null
}
